import React, { useState, useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown } from 'lucide-react';
import { ParallaxLink } from './ParallaxLink';

export const Hero = ({ onViewDomains }) => {
  const [countryCode, setCountryCode] = useState(null);

  useEffect(() => {
    // Fetch user's country code based on IP
    const fetchCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) throw new Error('API Rate Limit');
        const data = await response.json();
        if (data && data.country_code) {
          setCountryCode(data.country_code.toLowerCase());
          localStorage.setItem('user_country', data.country_code.toLowerCase());
        }
      } catch (error) {
        // Fallback to cached or default (US) to ensure UI consistency
        const cached = localStorage.getItem('user_country');
        setCountryCode(cached || 'us');
        console.warn('Using fallback country code:', error);
      }
    };

    fetchCountry();
  }, []);

  const scrollToDomains = () => {
    const domainsSection = document.getElementById('domains');
    if (domainsSection) {
      domainsSection.scrollIntoView({ behavior: 'smooth' });
    }
    onViewDomains && onViewDomains();
  };

  return (
    <section className="relative min-h-[50vh] flex items-center justify-center px-4 py-4 overflow-hidden">
      <div className="max-w-5xl mx-auto text-center space-y-4">
        {/* Label and Badge */}
        <div className="flex items-center justify-center gap-3 flex-wrap -mb-2">
          <span className="text-primary text-sm font-semibold tracking-wider uppercase">
            The XtreamCube Gate
          </span>
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/10">
            2026 Expansion
          </Badge>
        </div>

        {/* 3D Parallax Link Gateway */}
        <div className="flex justify-center">
          <ParallaxLink />
        </div>

        {/* Main Heading */}
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-2">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight">
              <span className="text-6xl sm:text-7xl lg:text-8xl text-primary">X</span>treamGate
            </h1>
            {countryCode && (
              <div className="relative top-1">
                <img
                  src={`https://flagcdn.com/w80/${countryCode}.png`}
                  srcSet={`https://flagcdn.com/w160/${countryCode}.png 2x`}
                  alt={`${countryCode} Flag`}
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full shadow-lg border-2 border-primary/20 object-cover"
                />
              </div>
            )}
          </div>
          <p className="text-sm sm:text-base text-primary font-semibold tracking-wider uppercase mb-4">
            Official Gateway
          </p>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            The official Xtreamcube services. Access verified domains safely.
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-0">
          <Button
            asChild
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 min-w-[200px]"
          >
            <a href="https://xtreamcube.shop" target="_blank" rel="noopener noreferrer">
              Go to XtreamCube Store
            </a>
          </Button>

          <Button
            onClick={scrollToDomains}
            size="lg"
            variant="outline"
            className="glass border-primary/30 hover:bg-primary/10 hover:text-foreground min-w-[200px]"
          >
            View All Backup Sites
          </Button>
        </div>

        {/* Mobile Quick Access / App Shortcut */}
        <div className="flex flex-col items-center justify-center md:hidden pt-3 gap-2">
          <button
            onClick={() => {
              if (window.deferredPWA) {
                window.deferredPWA.prompt();
                window.deferredPWA.userChoice.then((choiceResult) => {
                  if (choiceResult.outcome === 'accepted') {

                  }
                  window.deferredPWA = null;
                });
              } else {
                alert("To add to Home Screen:\n1. Tap the Share button (squares with arrow)\n2. Scroll down and tap 'Add to Home Screen'");
              }
            }}
            className="group relative w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/50 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:border-primary hover:shadow-[0_0_25px_rgba(34,197,94,0.4)]"
            style={{
              animation: 'spin-slow 8s linear infinite'
            }}
          >
            {/* Rotating border glow */}
            <span
              className="absolute inset-0 rounded-full"
              style={{
                background: 'conic-gradient(from 0deg, transparent, rgba(34,197,94,0.6), transparent)',
                animation: 'spin 3s linear infinite',
                opacity: 0.5
              }}
            />
            {/* Inner content */}
            <span className="relative z-10 text-2xl">📱</span>
          </button>
          <span className="text-xs font-semibold text-primary/80 uppercase tracking-wider">Save Shortcut</span>
        </div>

        {/* Note */}
        <p className="text-sm text-muted-foreground max-w-xl mx-auto pt-0">
          Your trusted gateway to verified XtreamCube domains.
        </p>

        {/* Scroll indicator */}
        <div className="pt-2">
          <button
            onClick={scrollToDomains}
            className="text-primary hover:text-primary/80 transition-colors animate-bounce"
            aria-label="Scroll to domains"
          >
            <ChevronDown className="w-8 h-8" />
          </button>
        </div>
      </div>
    </section>
  );
};